import { useState, useEffect, useRef, useCallback } from "react";
import { FLEX_COL_FILL } from "../../../../../themes/sbux/components";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";
import SbuxEnhancedTable from "../../../../../components/SbuxTable/SbuxEnhancedTable";
import TemporaryMsgDialog from "../../common/TemporaryMsgDialog";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import { columns, filterColumns } from "../temporaryMsg/columns";
import TemporaryMsgRow from "./TemporaryMsgRow";
import { Box } from "@mui/material";
import { subOnUpdateTemporaryMessages } from "../../../../../graphql/api";
import SbuxStackSnackbar from "../../../../../components/SbuxStackSnackbar";
import {
  TEMPORARYMGT_REFRESH_USER_MESSAGE,
  OPERATION,
} from "../../../../../constants/index";
import {
  updateTMData,
  createTMData,
  resetHighlight,
} from "../../../../../slices/temporaryMsgSlice";
import { logMessage } from "../../../../../utils/amplifyLogger";
import SbuxLoader from "../../../../../components/SbuxLoader";
import { getInstanceIdSelector } from "../../../../../selectors/userInstanceSelector";
import { getCognitoUserIdSelector } from "../../../../../selectors/authSelector";
import { getTemporaryMsgStatusSelector } from "../../../../../selectors/temporaryMsgSelector";

interface Subscription {
  unsubscribe: () => void;
}

interface TemporaryMsgItem {
  id?: string;
  status?: string;
  highLight?: boolean;
  lastUpdatedBy?: string;
  __operation?: string;
  [key: string]: unknown;
}

interface Props {
  temporaryMsgData: TemporaryMsgItem[];
  handleRefrehClick: () => void;
  filteredItem: string[];
  setFilteredItem: (items: string[]) => void;
  temporaryMsgReset: boolean;
  isAdmin: boolean;
}

const TemporaryMsgTabs = ({
  temporaryMsgData,
  handleRefrehClick,
  filteredItem,
  setFilteredItem,
  temporaryMsgReset,
  isAdmin,
}: Props) => {
  const cognitoUserId = useAppSelector(getCognitoUserIdSelector);
  const instanceId = useAppSelector(getInstanceIdSelector);
  const temporaryMsgStatus = useAppSelector(getTemporaryMsgStatusSelector);
  const isLoading = temporaryMsgStatus === "pending";

  const [openAddNew, setOpenAddNew] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [temporaryMsgFilterData, setTemporaryMsgFilterData] = useState<
    TemporaryMsgItem[]
  >([]);
  const [row, setRow] = useState<Record<string, unknown> | null>(null);
  const [updatedTemporaryId, setUpdatedTemporaryId] = useState<string | null>(
    null,
  );
  const [openTMUpdateSnackbar, setOpenTMUpdateSnackbar] = useState(false);

  const temporaryMsgDataRef = useRef<TemporaryMsgItem[]>(temporaryMsgData);
  const subscriptionRef = useRef<Subscription | null>(null);
  const dispatch = useAppDispatch();

  // Use ref to avoid stale closure issues with cognitoUserId
  const cognitoUserIdRef = useRef(cognitoUserId);
  useEffect(() => {
    cognitoUserIdRef.current = cognitoUserId;
  }, [cognitoUserId]);

  const filteredItemRef = useRef(filteredItem);
  useEffect(() => {
    filteredItemRef.current = filteredItem;
  }, [filteredItem]);

  const handleTemporarySubscription = useCallback(
    (data: { data?: { onProcessedTemporaryMessage?: TemporaryMsgItem } }) => {
      const temporaryData = data?.data?.onProcessedTemporaryMessage;
      if (!temporaryData) return;

      const operation = temporaryData.__operation;
      const lastUpdatedBy = temporaryData.lastUpdatedBy;
      const currentUserId = cognitoUserIdRef.current;

      const isOwnUpdate =
        currentUserId && lastUpdatedBy && lastUpdatedBy === currentUserId;
      if (!isOwnUpdate) {
        setUpdatedTemporaryId(temporaryData.id ?? null);

        switch (operation) {
          case OPERATION.CREATE:
            dispatch(
              createTMData(temporaryData as Parameters<typeof createTMData>[0]),
            );
            break;
          case OPERATION.UPDATE:
            dispatch(
              updateTMData(temporaryData as Parameters<typeof updateTMData>[0]),
            );
            break;
        }

        setOpenTMUpdateSnackbar(true);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    // Clean up existing subscription before creating a new one
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    // Don't subscribe if instanceId is not available
    if (!instanceId) {
      return;
    }

    const onProcessSub = subOnUpdateTemporaryMessages(instanceId).subscribe(
      (data: unknown) =>
        handleTemporarySubscription(
          data as Parameters<typeof handleTemporarySubscription>[0],
        ),
      (err: unknown) =>
        logMessage(
          `Temporary Messages`,
          `Something went wrong in Subscription API => ${String(err)}`,
          `error`,
        ),
    );

    subscriptionRef.current = onProcessSub;

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [instanceId, handleTemporarySubscription]);

  useEffect(() => {
    temporaryMsgDataRef.current = temporaryMsgData;
    if (filteredItem && !filteredItem?.includes("all")) {
      const filteredData = temporaryMsgData.filter((f: TemporaryMsgItem) =>
        filteredItem.includes(f.status ?? ""),
      );
      setTemporaryMsgFilterData(filteredData);
    } else {
      setTemporaryMsgFilterData(temporaryMsgData);
    }
  }, [temporaryMsgData, filteredItem]);

  const handleAddNew = () => {
    setOpenAddNew((prevState) => !prevState);
  };

  const handleEditRowClick = (row: Record<string, unknown>) => {
    setRow(row);
    setIsEditOpen((prevState) => !prevState);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
  };

  const handleClose = useCallback(() => {
    setOpenTMUpdateSnackbar(false);
    const updatedData = temporaryMsgDataRef.current.map((item) => {
      return { ...item, highLight: false };
    });
    dispatch(
      resetHighlight(updatedData as Parameters<typeof resetHighlight>[0]),
    );
    // Re-apply the active filter so closing the snackbar doesn't reset a filtered view
    const currentFilter = filteredItemRef.current;
    if (currentFilter && !currentFilter.includes("all")) {
      setTemporaryMsgFilterData(
        updatedData.filter((item) => currentFilter.includes(item.status ?? "")),
      );
    } else {
      setTemporaryMsgFilterData(updatedData);
    }
  }, [dispatch]);

  return (
    <Box sx={FLEX_COL_FILL}>
      {isLoading ? (
        <SbuxLoader />
      ) : (
        <SbuxEnhancedTable
          rows={
            temporaryMsgReset && filteredItem.length === 0
              ? []
              : (temporaryMsgFilterData as Record<string, unknown>[])
          }
          columns={
            columns as unknown as {
              id: string;
              label: string;
              [key: string]: unknown;
            }[]
          }
          defaultSortingField={"sequenceNumber"}
          defaultSortOrder={"asc"}
          RowSource={
            TemporaryMsgRow as unknown as React.ComponentType<{
              row: Record<string, unknown>;
              columns: { id: string; label: string; [key: string]: unknown }[];
              handleEditRowClick: (row: Record<string, unknown>) => void;
              handleDeleteRowClick: (row: Record<string, unknown>) => void;
              handle24HoursClick: (row: Record<string, unknown>) => void;
              updatedRowId: string | null;
            }>
          }
          showFooter={true}
          showSearchBar={true}
          isManager={true}
          filterColumns={filterColumns as Record<string, unknown>[]}
          defaultFilterColumns={filteredItem}
          setFilteredItem={setFilteredItem}
          filterTitle={<SbuxTranslate>{"Temporary Messages"}</SbuxTranslate>}
          showAddNewButton
          handleAddNewButtonClick={handleAddNew}
          addNewButtonTitle={<SbuxTranslate>{"Add New"}</SbuxTranslate>}
          handleEditRowClick={handleEditRowClick}
          handleRefreshClick={handleRefrehClick}
          isdisableAddNewButton={!isAdmin}
          updatedRowId={updatedTemporaryId}
          isLoading={isLoading}
          footerMarginBottom={"0"}
        />
      )}
      {openAddNew && (
        <TemporaryMsgDialog
          title={
            <SbuxTranslate>{"Create new temporary message"}</SbuxTranslate>
          }
          open={openAddNew}
          toggleDialogOpen={handleAddNew}
          row={{}}
          sequenceNumber={temporaryMsgData.length + 1}
          isAddNew
        />
      )}
      {isEditOpen && row && (
        <TemporaryMsgDialog
          title={<SbuxTranslate>{"Edit temporary message"}</SbuxTranslate>}
          open={isEditOpen}
          toggleDialogOpen={handleCloseEdit}
          row={row}
          isAddNew={false}
        />
      )}
      {openTMUpdateSnackbar && (
        <SbuxStackSnackbar
          width={380}
          open={openTMUpdateSnackbar}
          message={TEMPORARYMGT_REFRESH_USER_MESSAGE}
          autoHide={false}
          handleClose={handleClose}
        />
      )}
    </Box>
  );
};

export default TemporaryMsgTabs;
